import { Component, computed, effect, inject, signal, input, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';

import { RegisterComponent } from '../../components/component-lookup';
import { VisualBaseComponent } from '../visual-base.component';
import { FilterCorrelationMetricPipe } from '../../pipes/filter-correlation-metric.pipe';
import { GroupByPipe } from '../../../shared/pipes/group-by.pipe';

import { DateRange, StateService } from '../../services/state.service';
import { InstitutionStatistical } from '../../services/metric.service';
import { SessionService } from '../../../shared/services/session.service';
import { TableColumn, NgxDatatableModule } from '@swimlane/ngx-datatable';
import { DropDownService, RegressionDropdown } from '../../services/drop.down.service';
import { FilterRegressionMetricPipe } from "../../pipes/filter-regression-metric.pipe";
import { ChartInfoBoxComponent } from "../chart-info-box/chart-info-box.component";


@RegisterComponent('HeatTableComponent')
@Component({
  selector: 'app-heat-table',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, NgxDatatableModule, FilterCorrelationMetricPipe, GroupByPipe, FilterRegressionMetricPipe, ChartInfoBoxComponent],
  templateUrl: './heat-table.component.html',
  styleUrl: './heat-table.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class HeatTableComponent extends VisualBaseComponent {
  readonly institution = inject(StateService).institution;
  readonly dateRange = inject(StateService).dateRange;
  private dropDownService = inject(DropDownService);


  private sessionService = inject(SessionService);
  readonly regressionFormula = signal<string>('');

  readonly regressionMetrics = signal<RegressionDropdown[]>([]);
  readonly metric = signal<RegressionDropdown | undefined>(undefined);
  readonly correlationRates = computed(() => this.metric()?.rates ?? []);


  private statisticals?: InstitutionStatistical[];
  readonly statistical = signal<InstitutionStatistical | undefined>(undefined);
  readonly bestStatistical = signal<InstitutionStatistical | undefined>(undefined);
  readonly insufficientData = computed(() => this.statistical()?.beta == null);
  readonly dialogInformationId = input<string>('');

  metricListOpen = false;
  openMetricCategories: string[] = [];
  metricSearch = '';
  noData = false;
  minRsquared = 0;
  maxRsquared = 0;
  isLoading = true;
  onlyOneCategory = signal<boolean>(false);
  sortColumn: string = '';
  sortDir: string = '';

  tableRows: any[] = [];  // Array to hold data for ngx-datatable
  columns: TableColumn[] = [
    { name: 'Rate', sortable: true },
    { name: 'Lag', sortable: true },
    { name: 'R2', sortable: true },
    { name: 'Beta', sortable: true }
  ];

  constructor() {
    super();

    effect(() => {
      const institution = this.institution();
      if (institution?.institutionType) {
        this.dropDownService.getRegressionDropdown().subscribe(metrics => {
          setTimeout(() => {
            this.regressionMetrics.set(metrics);
            this.onlyOneCategory.set(this.hasOnlyOneDistinctValue(metrics, 'categoryTitle'));
            const metricId = this.sessionService
              .get(`regression-metricId-${this.institution()?.institutionType}`)
              ?.toString() ?? this.metric()?.metricId ?? metrics[0]?.metricId;
            this.selectMetric(metricId);

            this.sortColumn = this.sessionService.get("heatSortColumn")?.toString() ?? 'r2';
            this.sortDir = this.sessionService.get("heatSortDirection")?.toString() ?? 'desc';
          });
        });
      }
    });

    effect(() => {
      const dateRange = this.dateRange();
      const metric = this.metric();
      if (dateRange && metric) {
        this.isLoading = true
        this.updateVisual(dateRange, metric);

      }
    });
  }

  public updateVisual(dateRange: DateRange, metric: RegressionDropdown) {
    this.metricService.getInstitutionStatisticals(this.institution().iid, metric.metricId, dateRange, 'non-maturity-deposits').subscribe(statisticals => {
      this.statisticals = statisticals;

      this.metricService.getMetricData(this.institution().iid, metric.metricId, undefined)
        .subscribe(metricData => {
          const metricDataSeries = this.visualService.getMetricSeriesPeer(this.metric()?.displayType, metricData, dateRange);

          if (metricDataSeries.institutionSeries.filter(v => v != null).length == 0) {
            this.echartsInstance?.clear();
            this.chartOptions = this.noDataOptions;
            this.noData = true;
          }
          else {
            if (this.noData) {
              this.setOptions();
              this.noData = false;
            }

            const rateNameToCoordinates: Record<string, string> = {
              '10-year Treasury': '0-mo',
              '10-year Treasury (3-mo lag)': '3-mo',
              '10-year Treasury (6-mo lag)': '6-mo',
              '5-year Treasury': '0-mo',
              '5-year Treasury (3-mo lag)': '3-mo',
              '5-year Treasury (6-mo lag)': '6-mo',
              '3-month Treasury': '0-mo',
              '3-month Treasury (3-mo lag)': '3-mo',
              '3-month Treasury (6-mo lag)': '6-mo',
              'Fed Funds': '0-mo',
              'Fed Funds (3-Mo Lag)': '3-mo',
              'Fed Funds (6-Mo Lag)': '6-mo',
            };


            const rateBetaMap: Record<string, number | string> =
              this.statisticals?.reduce<Record<string, number | string>>((map, rate) => {
                map[rate.rateId] = map[rate.rateId] = rate.beta !== undefined ? rate.beta.toFixed(2) : '-';
                return map;
              }, {}) || {};

            const rateRsquaredMap: Record<string, number | string> =
              this.statisticals?.reduce<Record<string, number | string>>((map, rate) => {
                map[rate.rateId] = map[rate.rateId] = rate.rSquared !== undefined ? rate.rSquared.toFixed(2) : '-';
                return map;
              }, {}) || {};

            const rsquaredValues = Object.values(rateRsquaredMap)
              .filter(value => value !== '-' && !isNaN(Number(value)))
              .map(value => Number(value));

            this.minRsquared = rsquaredValues.length > 0 ? Math.min(...rsquaredValues) : 0;
            this.maxRsquared = rsquaredValues.length > 0 ? Math.max(...rsquaredValues) : 0;

            const rows = this.correlationRates().map(({ rateIdStatistical, rateName }) => {
              const cleanRateName = rateName.replace(/\(.*\)/, '').trim();
              const beta = rateBetaMap[rateIdStatistical] ?? '-';
              const rsquared = rateRsquaredMap[rateIdStatistical] ?? '-';
              const lag = rateNameToCoordinates[rateName.trim()] ? rateNameToCoordinates[rateName.trim()] : '-';


              return {
                rate: cleanRateName,
                lag: lag,
                beta: beta,
                r2: rsquared
              };
            });

            // Set the table rows
            this.tableRows = rows;
            this.isLoading = false;

          }
        });

    });

  }

  onSort(event: any) {
    if (event && event.sorts && event.sorts.length) {
      this.sortColumn = event.sorts[0].prop;
      this.sortDir = event.sorts[0].dir;
      this.sessionService.setAndWait("heatSortColumn", this.sortColumn).subscribe(() => {
        this.sessionService.set("heatSortDirection", this.sortDir, true)
      })
    }
  }

  getHeatmapColor(value: number): string {
    const maxValue = this.maxRsquared; // Dynamic max value
    const minValue = this.minRsquared; // Dynamic min value

    // Ensure value stays within the defined range
    const clampedValue = Math.min(Math.max(value, minValue), maxValue);

    // Normalize value to a range of 0 to 1
    const normalizedValue = (clampedValue - minValue) / (maxValue - minValue);

    // Define custom colors in hex
    const colorLow = "#bb3a67";  // Red (Low value)
    const colorMid = "#e7ca74";  // Yellow (Middle value)
    const colorHigh = "#2b821d"; // Green (High value)

    if (minValue === maxValue) {
      return colorHigh;
    }
    let color1, color2, t;

    if (normalizedValue <= 0.5) {
      // Interpolate between colorLow (red) and colorMid (yellow)
      color1 = this.hexToRgb(colorLow);
      color2 = this.hexToRgb(colorMid);
      t = normalizedValue * 2; // Scale to 0-1
    } else {
      // Interpolate between colorMid (yellow) and colorHigh (green)
      color1 = this.hexToRgb(colorMid);
      color2 = this.hexToRgb(colorHigh);
      t = (normalizedValue - 0.5) * 2; // Scale to 0-1
    }

    // Interpolate between the two colors
    const interpolatedColor = this.interpolateColor(color1, color2, t);

    return `rgb(${interpolatedColor.r}, ${interpolatedColor.g}, ${interpolatedColor.b})`;
  }

  // Convert hex color to RGB
  private hexToRgb(hex: string): { r: number, g: number, b: number } {
    const bigint = parseInt(hex.slice(1), 16);
    return {
      r: (bigint >> 16) & 255,
      g: (bigint >> 8) & 255,
      b: bigint & 255
    };
  }

  hasOnlyOneDistinctValue<T>(list: T[], property: keyof T): boolean {
    const uniqueValues = new Set(list.map((item) => item[property]));
    return uniqueValues.size === 1;
  }

  // Linear interpolation between two colors
  private interpolateColor(color1: { r: number, g: number, b: number }, color2: { r: number, g: number, b: number }, t: number): { r: number, g: number, b: number } {
    return {
      r: Math.round(color1.r + (color2.r - color1.r) * t),
      g: Math.round(color1.g + (color2.g - color1.g) * t),
      b: Math.round(color1.b + (color2.b - color1.b) * t)
    };
  }


  clearMetricSearch() {
    this.metricSearch = '';
    this.openMetricCategories = [];
  }


  isMetricCategoryOpen(category: string) {
    return this.openMetricCategories.find(c => c == category);
  }



  toggleMetricCategory(category: string) {
    if (this.openMetricCategories.find(c => c == category)) {
      this.openMetricCategories = this.openMetricCategories.filter(c => c != category);
    }
    else {
      this.openMetricCategories.push(category);
    }
  }


  selectMetric(metricId: string): void {
    const metric = this.regressionMetrics().find(m => m.metricId == metricId);
    if (metric) {
      this.sessionService.set(`regression-metricId-${this.institution()?.institutionType}`, metricId, true);
      this.metric.set(metric);
      this.metricListOpen = false;
    }
  }
}

